import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Alert, 
  CircularProgress, 
  Button
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import DataTable from '../common/DataTable';
import CompactFilterPanel from '../common/CompactFilterPanel';
import { FilterCriteria } from '../../types/company';
import { ApiService } from '../../services/api';
import { getErrorSeverity } from '../../utils/errorUtils';
import { useEditMode } from '../../hooks/useEditMode';
import { EditModeConfig } from '../../types/editMode';
import {
  EditModeControls,
  DeleteConfirmationDialog,
  RefreshNotificationDialog,
  EditWarningDialog,
} from '../common/editMode';
import { useEditWarning } from '../../hooks/useEditWarning';
import { usePermissions } from '../../hooks/usePermissions';

interface State {
  id: number;
  id_pais?: number;
  name: string;
  abbreviation?: string;
}

interface StateResponse {
  data: State[];
  pagination: {
    total: number;
    hasMore: boolean;
    nextCursor: number | null;
    limit: number;
  };
}

interface StateTableColumn {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: any) => string;
  type?: 'boolean' | 'text' | 'number';
  visible: boolean;
}

interface SortConfig {
  column: string;
  direction: 'ASC' | 'DESC';
}

const StateView: React.FC = () => {
  const { canEdit } = usePermissions();
  
  const [states, setStates] = useState<State[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    hasMore: false,
    nextCursor: null as number | null,
    limit: 50
  });
  const [appliedFilters, setAppliedFilters] = useState<FilterCriteria[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    column: 'id',
    direction: 'ASC'
  });
  const scrollPositionRef = useRef<number>(0);

  const allColumns: StateTableColumn[] = [
    { id: 'id', label: 'ID', minWidth: 80, type: 'number', visible: true },
    { id: 'id_pais', label: 'Country ID', minWidth: 120, type: 'number', visible: true },
    { id: 'name', label: 'State Name', minWidth: 200, type: 'text', visible: true },
    { id: 'abbreviation', label: 'Abbreviation', minWidth: 150, type: 'text', visible: true },
  ];

  const [visibleColumns, setVisibleColumns] = useState(
    allColumns.map(col => ({ id: col.id, label: col.label, visible: col.visible }))
  );

  const columns = allColumns.filter(col => 
    visibleColumns.find(vc => vc.id === col.id)?.visible
  );

  const loadStates = useCallback(async (
    cursor: number | null, 
    isNextPage: boolean,
    filters: FilterCriteria[],
    refresh?: boolean
  ) => {
    if (isNextPage) {
      setIsLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      let response: StateResponse;
      
      if (filters.length > 0) {
        response = await ApiService.searchStates({
          filters,
          cursor,
          limit: pagination.limit,
          sort: sortConfig.column,
          direction: sortConfig.direction,
          refresh
        });
      } else {
        response = await ApiService.getStates(
          cursor,
          pagination.limit,
          sortConfig.column,
          sortConfig.direction,
          refresh
        );
      }

      if (isNextPage) {
        setStates(prev => [...prev, ...response.data]);
        
        setTimeout(() => {
          if (scrollPositionRef.current > 0) {
            window.scrollTo(0, scrollPositionRef.current);
          }
        }, 0);
      } else {
        setStates(response.data);
      }

      setPagination({
        total: response.pagination.total,
        hasMore: response.pagination.hasMore,
        nextCursor: response.pagination.nextCursor,
        limit: response.pagination.limit
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load states');
      console.error('Error loading states:', err);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, [pagination.limit, sortConfig]);

  // Initial load
  useEffect(() => {
    loadStates(null, false, appliedFilters);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  const handleLoadMore = () => {
    if (pagination.hasMore && !isLoadingMore && !loading) {
      // Store current scroll position
      scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;
      
      loadStates(pagination.nextCursor, true, appliedFilters);
    }
  };

  const handleSort = useCallback((column: string) => {
    // Determine new sort direction
    setSortConfig(prev => {
      const newDirection = prev.column === column && prev.direction === 'ASC' ? 'DESC' : 'ASC';
      return {
        column: column,
        direction: newDirection
      };
    });
    
    // Clear existing data and reset pagination
    setStates([]);
    setPagination(prev => ({ ...prev, nextCursor: null }));
    
    // Reload data with new sort
    setTimeout(() => {
      loadStates(null, false, appliedFilters);
    }, 0);
  }, [loadStates, appliedFilters]);

  const handleColumnToggle = (columnId: string) => {
    setVisibleColumns(prev =>
      prev.map(col =>
        col.id === columnId ? { ...col, visible: !col.visible } : col
      )
    );
  };

  const handleFiltersApply = (filters: FilterCriteria[]) => {
    setAppliedFilters(filters);
    setPagination(prev => ({ ...prev, nextCursor: null }));
  };

  const handleClearFilters = () => {
    setAppliedFilters([]);
    setPagination(prev => ({ ...prev, nextCursor: null }));
  };

  // Use the edit warning hook
  const editWarning = useEditWarning('editWarningShown_global', () => {
    editMode.handleEdit();
  });

  const handleEditClick = () => {
    if (editMode.isEditMode) {
      editMode.handleCancel();
      return;
    }
    editWarning.handleEditClick();
  };

  // Edit Mode Configuration
  const editModeConfig: EditModeConfig<State> = {
    apiMethods: {
      create: (data: any) => ApiService.createState(data),
      update: (id: number, data: any) => ApiService.updateState(id, data),
      delete: (id: number) => ApiService.deleteState(id),
    },
    fields: {
      id: { type: 'number', required: false, editable: false },
      id_pais: { type: 'number', required: false, editable: true, defaultValue: null },
      name: { type: 'text', required: true, editable: true, defaultValue: '' },
      abbreviation: { type: 'text', required: false, editable: true, defaultValue: null },
    },
    primaryKey: 'id',
    displayName: 'State',
    validate: (row, isNew) => {
      if (isNew) {
        const nameValue = row.name;
        const trimmedName = nameValue ? String(nameValue).trim() : '';
        if (!trimmedName) {
          return 'Name is required for new states';
        }
      }
      return null;
    },
    prepareData: (row, originalRow) => {
      // For new rows, ensure all fields are properly set
      if (!originalRow) {
        return {
          id_pais: row.id_pais !== undefined && row.id_pais !== null ? row.id_pais : undefined,
          name: row.name ? String(row.name).trim() : '',
          abbreviation: row.abbreviation !== undefined && row.abbreviation !== null ? row.abbreviation : undefined,
        };
      }
      
      // OPTIMIZATION: For updates, ONLY process fields that are in the row (changed fields)
      // This dramatically reduces processing time when only 1-2 fields are changed
      const updateData: Partial<State> = {};
      
      // Only process fields that actually changed (exist in row)
      Object.keys(row).forEach(key => {
        const fieldKey = key as keyof State;
        
        if (fieldKey === 'name') {
          updateData.name = row.name ? String(row.name).trim() : '';
        } else if (fieldKey === 'id_pais') {
          updateData.id_pais = row.id_pais !== null ? row.id_pais : undefined;
        } else if (fieldKey === 'abbreviation') {
          updateData.abbreviation = row.abbreviation !== null ? row.abbreviation : undefined;
        }
      });
      
      return updateData;
    },
  };

  // Use the edit mode hook
  const editMode = useEditMode(editModeConfig, {
    data: states,
    onDataChange: setStates,
    onRefresh: async () => {
      await loadStates(null, false, appliedFilters);
    },
    onError: (errorMessage) => {
      setError(errorMessage);
    },
  });


  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: '100%',
      display: 'flex', 
      flexDirection: 'column',
      overflowX: 'hidden'
    }}>
      {/* Header Section */}
      <Box sx={{ 
        flexShrink: 0,
        backgroundColor: 'white',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        mb: 0.5
      }}>
        
        {error && (
          <Alert severity={getErrorSeverity(error)} sx={{ m: 2, mt: 0 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <CompactFilterPanel
              columns={visibleColumns}
              onColumnToggle={handleColumnToggle}
              onFiltersApply={handleFiltersApply}
              onClearFilters={handleClearFilters}
            onEdit={canEdit ? handleEditClick : undefined}
              onAddNew={() => {}}
              filterType="state"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading...
                </Typography>
              </Box>
            )}
            <Button
              variant="outlined"
              onClick={() => {
                // Exit edit mode if active
                if (editMode.isEditMode) {
                  editMode.handleCancel();
                }
                // Reload data with cache refresh
                loadStates(null, false, appliedFilters, true);
              }}
              sx={{ minWidth: 'auto', padding: '8px' }}
            >
              <RefreshIcon />
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Edit Mode Controls - Visible for all users */}
      {canEdit && (
        <EditModeControls
          isEditMode={editMode.isEditMode}
          saving={editMode.saving}
          onSave={editMode.handleSave}
          onCancel={editMode.handleCancel}
          onAddNew={editMode.handleAddNew}
          selectedRowsRef={editMode.selectedRowsRef}
          onDeleteClick={editMode.handleDeleteClick}
          deleteDialogRef={editMode.deleteDialogRef}
          refreshNotificationRef={editMode.refreshNotificationRef}
        />
      )}
      
      {/* Table Section */}
      <Box sx={{ 
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        mb: 3
      }}>
        <Box sx={{ 
          width: '100%',
          maxWidth: '100%',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          overflow: 'hidden', // Clip content, scrolling handled by TableContainer
        }}>
          <DataTable
            columns={columns}
            rows={editMode.displayRows}
            page={0}
            rowsPerPage={editMode.displayRows.length} // Show all loaded records
            totalRows={pagination.total}
            onPageChange={() => {}}
            onRowsPerPageChange={() => {}}
            isEditMode={editMode.isEditMode}
            onCellEdit={editMode.handleCellEdit}
            isRowSelected={editMode.isRowSelected}
            selectedRowsCount={editMode.selectedRowsCount}
            selectAllVersion={editMode.selectAllVersion}
            onRowSelect={editMode.handleRowSelect}
            onSelectAll={editMode.handleSelectAll}
            hidePagination={true}
            {...({ onSort: handleSort, sortConfig } as any)}
          />
        </Box>
      </Box>

      {/* Delete Confirmation Dialog - Visible for all users */}
      {canEdit && (
        <DeleteConfirmationDialog
          dialogRef={editMode.deleteDialogRef}
          selectedRowsRef={editMode.selectedRowsRef}
          onConfirm={editMode.handleConfirmDelete}
        />
      )}

      {/* Refresh Notification Dialog - Visible for all users */}
      {canEdit && (
        <RefreshNotificationDialog
          dialogRef={editMode.refreshNotificationRef}
        />
      )}

      {/* Edit Warning Dialog */}
      <EditWarningDialog
        open={editWarning.showWarning}
        onClose={editWarning.handleCancel}
        onProceed={editWarning.handleProceed}
      />

      {/* Load More Section - Right Aligned */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 2,
        mb: 3, 
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden'
      }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          {states.length} of {pagination.total}
        </Typography>
        
        {isLoadingMore ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1
          }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        ) : pagination.hasMore ? (
          <button 
            onClick={handleLoadMore}
            style={{
              padding: '6px 16px',
              fontSize: '0.75rem',
              fontWeight: 500,
              color: '#1976d2',
              backgroundColor: 'transparent',
              border: '1px solid #1976d2',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(25, 118, 210, 0.04)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Load More
          </button>
        ) : states.length > 0 ? (
          <Typography variant="caption" color="text.secondary">
            No more records
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default StateView;

